import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import Footer from '../components/Footer';
import './BlogPost.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BlogPost() {
  const { postId } = useParams();
  const [user, setUser] = useState(null);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const language = localStorage.getItem('language') || 'en';
  const t = translations[language];
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      // User is logged in, we can enable commenting
      setUser({ token });
    }
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/blog/${postId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog post');
      }
      const data = await response.json();
      setPost(data.post);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/${postId}/comments`);
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      const data = await response.json();
      setComments(data.comments);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login to comment');
      navigate('/auth');
      return;
    }

    if (!newComment.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/blog/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newComment })
      });

      if (!response.ok) {
        throw new Error('Failed to post comment');
      }

      setNewComment('');
      await fetchComments();
    } catch (err) {
      alert('Failed to post comment: ' + err.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-post-container">
        <div className="loading">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-container">
        <div className="error-message">{error || 'Post not found'}</div>
        <button onClick={() => navigate('/blog')} className="back-to-blog-btn">
          {t.backToBlog}
        </button>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-post-container">
      <div className="blog-post-header">
        <button onClick={() => navigate('/blog')} className="back-btn">
          ← {t.backToBlog}
        </button>
      </div>

      <article className="blog-post-content">
        {post.featured_image && (
          <div className="featured-image">
            <img src={post.featured_image} alt={post.title} />
          </div>
        )}

        <h1 className="post-title">{post.title}</h1>

        <div className="post-meta">
          <span className="author">By {post.author_name}</span>
          <span className="date">{new Date(post.created_at).toLocaleDateString()}</span>
        </div>

        <div 
          className="post-body"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      <section className="comments-section">
        <h2>{t.comments} ({comments.length})</h2>

        {user && (
          <form onSubmit={handleSubmitComment} className="comment-form">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t.writingComment}
              rows="4"
              disabled={submittingComment}
            />
            <button 
              type="submit" 
              disabled={submittingComment || !newComment.trim()}
              className="submit-comment-btn"
            >
              {submittingComment ? t.sending : t.postComment}
            </button>
          </form>
        )}

        {!user && (
          <div className="login-prompt">
            <p>{t.addComment}</p>
            <button onClick={() => navigate('/auth')} className="login-btn">
              {t.login}
            </button>
          </div>
        )}

        <div className="comments-list">
          {comments.length === 0 ? (
            <p className="no-comments">{t.noComments}</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="comment">
                <div className="comment-header">
                  <strong>{comment.user_name}</strong>
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="comment-content">{comment.content}</p>
              </div>
            ))
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogPost;
